import type { ImageData } from '@jobilla/nexus-sdk'
import type { CachedImage, CacheImageUrl } from './images/image-cache'

export type ReadinessImage = Pick<ImageData, 'isPlaceholder' | 'isGenerating'> | null | undefined

/**
 * A missing image counts as ready so the editor renders its empty state instead
 * of a generating one.
 */
export function isImageReady(image: ReadinessImage): boolean {
	return !image?.isPlaceholder && !image?.isGenerating
}

export function isImageEmpty(image: ReadinessImage): boolean {
	return !!image?.isPlaceholder && !image.isGenerating
}

export type EditorImage = Pick<ImageData, 'id' | 'url'> & { isReady: boolean; isEmpty?: boolean }

/**
 * While an image is still generating, the editor keeps showing the newest
 * finished image from the history underneath the loading state. History is
 * newest first and may contain the generating image itself.
 */
function resolveDisplayedImage(image: EditorImage, imagesHistory: Array<EditorImage> | undefined): CachedImage {
	if (image.isReady) {
		return { url: image.url, isReady: true }
	}

	const previous = imagesHistory?.find((candidate) => candidate.isReady && candidate.id !== image.id)

	return { url: previous?.url ?? '', isReady: false }
}

export function cacheEditorImages(
	image: EditorImage | undefined,
	imagesHistory: Array<EditorImage> | undefined,
	cacheImageUrl: CacheImageUrl,
): void {
	if (image?.isEmpty) {
		cacheImageUrl(image.id, '', true, true)
	} else if (image) {
		const displayed = resolveDisplayedImage(image, imagesHistory)
		cacheImageUrl(image.id, displayed.url, displayed.isReady)
	}

	imagesHistory?.forEach((historyImage) => {
		if (historyImage.id === image?.id) {
			return
		}

		cacheImageUrl(historyImage.id, historyImage.isReady ? historyImage.url : '', historyImage.isReady)
	})
}
